import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../components/Utils/ModalShow";
import { FaUpload, FaCheck, FaExclamationCircle, FaSearch } from "react-icons/fa";
import * as XLSX from "xlsx";
import { apiService } from "../../../../components/Config/api.service";

export const DEFAULT_ACCOUNT_KEYS = {
  "prime imposable": "prime_imposable",
  "prime non imposable": "prime_non_imposable",
  "avantage": "avantage",
  "deduction": "deduction",
  "note de frais": "note",
  "salaire stagiaire/prestataire": "external",
  "taxe stagiaire prestataire": "external_tax"
};

// supportsDryRun : le serveur sait vérifier sans écrire. Un serveur antérieur ignorerait
// `dry_run` et importerait pour de bon : dans ce cas on garde l'import direct.
const ImportAccountingModal = ({ isOpen, onClose, onSuccess, supportsDryRun = true }) => {
  const { t } = useTranslation();
  const FORM_FIELDS = [
    { key: "id", label: "ID" },
    { key: "name", label: t('importAccountingModal.colName') },
    { key: "code", label: t('importAccountingModal.colCode') },
    { key: "account_debit", label: t('importAccountingModal.colDebitAccount') },
    { key: "account_credit", label: t('importAccountingModal.colCreditAccount') },
  ];
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState("select"); // select | mapping | preview | report | result
  // Compte rendu ligne par ligne renvoyé par le serveur avant validation (rien n'est encore écrit).
  const [report, setReport] = useState(null);
  const [reportFilter, setReportFilter] = useState("all");
  const [reportSearch, setReportSearch] = useState("");
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");

  const handleDownloadTemplate = async () => {
    try {
      const res = await apiService.getElement("/accounting/codes/export?include_defaults=true");
      if (res.data && res.data.file) {
        const link = document.createElement("a");
        link.href = "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," + res.data.file;
        link.download = res.data.filename || "Modele_Import_Comptabilite.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        setError(t('importAccountingModal.templateDownloadImpossible'));
      }
    } catch (err) {
      setError(t('importAccountingModal.templateDownloadError', { message: err.message || t('importAccountingModal.unknown') }));
    }
  };

  // Lecture du fichier et détection des colonnes
  const handleFileChange = async e => {
    setFile(e.target.files[0]);
    setPreview(null);
    setError("");
    setStep("select");
    setColumns([]);
    setMapping({});
    if (e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = evt => {
        try {
          const workbook = XLSX.read(evt.target.result, { type: "binary" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(sheet, { defval: "" });
          if (!data.length) throw new Error(t('importAccountingModal.emptyFile'));
          const fileColumns = Object.keys(data[0]);
          setColumns(fileColumns);

        //   // Mapping automatique par nom identique (case insensitive)
        //   const autoMapping = {};
        //   FORM_FIELDS.forEach(field => {
        //     const found = fileColumns.find(
        //       col => col.toLowerCase() === field.key.toLowerCase()
        //     );
        //     if (found) autoMapping[field.key] = found;
        //   });
        //   setMapping(autoMapping);
          const defaultRows = [];
          const ruleRows = [];
          
          data.forEach(row => {
            let isDefault = false;
            Object.values(row).forEach(val => {
              const valStr = String(val || "").toLowerCase();
              if (valStr.startsWith("default_") || valStr.includes("code par défaut") || valStr.includes("code par defaut")) {
                isDefault = true;
              }
            });
            
            if (isDefault) {
              defaultRows.push(row);
            } else {
              ruleRows.push(row);
            }
          });
          
          const previewRows = [...defaultRows, ...ruleRows.slice(0, 15)];
          setPreview({ columns: fileColumns, rows: previewRows, allRows: data });
          setStep("mapping");
        } catch (err) {
          setError(t('importAccountingModal.readError', { message: err.message }));
        }
      };
      reader.readAsBinaryString(e.target.files[0]);
    }
  };

  // Modification du mapping
  const handleMappingChange = (fieldKey, col) => {
    setMapping(m => ({ ...m, [fieldKey]: col }));
  };

  // Prévisualisation avec le mapping choisi
  const handlePreview = () => {
    setStep("preview");
  };

  // Validation finale : on remplit le form d'Accounting
  // dryRun : le serveur calcule le compte rendu sans rien écrire ; l'import réel suit la confirmation.
  const handleImport = async (dryRun = false) => {
    if (!preview) return;
    setImporting(true);
    setError("");
    const rules = [];
    const default_accounts = {};

    preview.allRows.forEach((row, rowIndex) => {
      // Numéro de ligne dans le fichier (la ligne 1 porte les en-têtes).
      const mapped = { row_number: rowIndex + 2 };
      FORM_FIELDS.forEach(field => {
        const col = mapping[field.key];
        mapped[field.key] = col ? row[col] : "";
      });

      // Align isDefault check with preview check for absolute robustness
      let isDefault = false;
      Object.values(row).forEach(val => {
        const valStr = String(val || "").toLowerCase();
        if (valStr.startsWith("default_") || valStr.includes("code par défaut") || valStr.includes("code par defaut")) {
          isDefault = true;
        }
      });

      // Fallback check on mapped fields just in case
      if (!isDefault) {
        isDefault = (mapped.id || "").toString().startsWith("default_") || 
                    (mapped.name || "").toLowerCase().startsWith("code par défaut") ||
                    (mapped.name || "").toLowerCase().startsWith("code par defaut");
      }

      if (isDefault) {
        // Resolve the API key
        // 1. Try to find raw ID starting with "default_" in the excel row cells
        let rawId = "";
        Object.values(row).forEach(val => {
          const valStr = String(val || "");
          if (valStr.toLowerCase().startsWith("default_")) {
            rawId = valStr;
          }
        });

        const idValue = rawId || mapped.id || "";
        const idStr = idValue.toString().replace("default_", "").trim().toLowerCase();
        let apiKey = "";

        apiKey = DEFAULT_ACCOUNT_KEYS[idStr] || "";
        // 2. Fallback on name
        if (!apiKey) {
          let rawName = "";
          Object.values(row).forEach(val => {
            const valStr = String(val || "");
            if (valStr.toLowerCase().includes("code par défaut") || valStr.toLowerCase().includes("code par defaut")) {
              rawName = valStr;
            }
          });
          
          const name = (mapped.name || rawName || "").toLowerCase();
          if (name.includes("prime non imposable")) apiKey = "prime_non_imposable";
          else if (name.includes("prime imposable")) apiKey = "prime_imposable";
          else if (name.includes("avantage")) apiKey = "avantage";
          else if (name.includes("deduction") || name.includes("déduction") || name.includes("defaut deduction") || name.includes("defaut déduction")) apiKey = "deduction";
          else if (name.includes("note")) apiKey = "note";
          else if (name.includes("stagiaire") && name.includes("taxe")) apiKey = "external_tax";
          else if (name.includes("stagiaire") || name.includes("prestataire")) apiKey = "external";
        }

        if (apiKey) {
          default_accounts[apiKey] = {
            debit: mapped.account_debit || "",
            credit: mapped.account_credit || "",
            row_number: mapped.row_number,
          };
        }
      } else {
        rules.push(mapped);
      }
    });

    try {
      const res = await apiService.saveElement("/accounting/codes/import", { rules, default_accounts, dry_run: dryRun });
      if (res.data && dryRun && res.data.dry_run !== true) {
        // Le serveur n'a pas reconnu la vérification : il a importé. On le dit plutôt
        // que d'afficher un compte rendu vide.
        setWarnings(res.data.warnings || []);
        setSuccessMsg(t('importAccountingModal.importCompleted', { count: res.data.updated || 0 }));
        setStep("result");
        onSuccess?.();
      } else if (res.data && dryRun) {
        setReport(res.data);
        setReportFilter("all");
        setReportSearch("");
        setStep("report");
      } else if (res.data) {
        if (res.data.warnings && res.data.warnings.length > 0) {
          setWarnings(res.data.warnings);
          setSuccessMsg(t('importAccountingModal.importCompleted', { count: res.data.updated || 0 }));
          setStep("result");
          onSuccess?.();
        } else {
          onSuccess?.();
          // Reset all data and remove loaded file after import
          setFile(null);
          setColumns([]);
          setMapping({});
          setPreview(null);
          setReport(null);
          setStep("select");
          setError("");
          onClose();
        }
      }
    } catch (err) {
      setError(t('importAccountingModal.importError', { message: err.message || t('importAccountingModal.unknown') }));
    } finally {
      setImporting(false);
    }
  };

  const reportLines = report?.report || [];
  const reportChangeCount = reportLines.filter((line) => line.changes.length > 0).length;
  const normalizeText = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const visibleReportLines = reportLines.filter((line) => {
    if (reportFilter !== "all" && line.status !== reportFilter) return false;
    const term = normalizeText(reportSearch.trim());
    return !term || normalizeText(`${line.name} ${line.code} ${line.unknown_codes.join(" ")} ${line.changes.map((c) => `${c.from} ${c.to}`).join(" ")}`).includes(term);
  });
  const reportStatuses = [
    { id: "updated", tone: "bg-green-50 text-green-800 border-green-200" },
    { id: "unchanged", tone: "bg-gray-50 text-gray-700 border-gray-200" },
    { id: "not_found", tone: "bg-orange-50 text-orange-800 border-orange-200" },
    { id: "unknown_account", tone: "bg-red-50 text-red-800 border-red-200" },
  ];
  const statusTone = Object.fromEntries(reportStatuses.map((status) => [status.id, status.tone]));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('importAccountingModal.title')}
      headerIcon={<FaUpload />}
      footer={
        step === "preview" ? (
          <button
            className="px-6 py-2 rounded bg-[--color-primary] text-white flex items-center gap-2 disabled:opacity-60"
            onClick={() => handleImport(supportsDryRun)}
            disabled={importing}
            data-cy="import-check"
          >
            {importing ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                {supportsDryRun ? t('importAccountingModal.checkInProgress') : t('importAccountingModal.importInProgress')}
              </>
            ) : (
              <>
                {supportsDryRun ? <FaSearch /> : <FaCheck />}
                {supportsDryRun ? t('importAccountingModal.checkBeforeImport') : t('importAccountingModal.importThisData')}
              </>
            )}
          </button>
        ) : step === "report" && report ? (
          <button
            className="px-6 py-2 rounded bg-green-600 text-white flex items-center gap-2 disabled:opacity-60"
            onClick={() => handleImport(false)}
            disabled={importing || reportChangeCount === 0}
            data-cy="import-confirm"
          >
            {importing ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                {t('importAccountingModal.importInProgress')}
              </>
            ) : (
              <>
                <FaCheck />
                {t('importAccountingModal.confirmImport', { count: reportChangeCount })}
              </>
            )}
          </button>
        ) : null
      }
    >
      {/* Stepper UX */}
      <div className="flex items-center justify-center mb-6 gap-4 text-xs font-semibold">
        <div className={`flex items-center gap-1 ${step === "select" ? "text-[--color-primary]" : "text-gray-400"}`}>
          <span className="rounded-full w-5 h-5 flex items-center justify-center border border-[--color-primary]">{step === "select" ? 1 : <FaCheck size={12} />}</span>
          {t('importAccountingModal.stepFile')}
        </div>
        <div className="h-0.5 w-8 bg-gray-200" />
        <div className={`flex items-center gap-1 ${step === "mapping" ? "text-[--color-primary]" : "text-gray-400"}`}>
          <span className="rounded-full w-5 h-5 flex items-center justify-center border border-[--color-primary]">{step === "mapping" ? 2 : ["preview", "report", "result"].includes(step) ? <FaCheck size={12} /> : ""}</span>
          {t('importAccountingModal.stepMapping')}
        </div>
        <div className="h-0.5 w-8 bg-gray-200" />
        <div className={`flex items-center gap-1 ${["preview", "report"].includes(step) ? "text-[--color-primary]" : "text-gray-400"}`}>
          <span className="rounded-full w-5 h-5 flex items-center justify-center border border-[--color-primary]">{["preview", "report"].includes(step) ? 3 : step === "result" ? <FaCheck size={12} /> : ""}</span>
          {t('importAccountingModal.stepPreview')}
        </div>
        <div className="h-0.5 w-8 bg-gray-200" />
        <div className={`flex items-center gap-1 ${step === "result" ? "text-yellow-600" : "text-gray-400"}`}>
          <span className="rounded-full w-5 h-5 flex items-center justify-center border border-[--color-primary]">{step === "result" ? <FaExclamationCircle size={12} /> : ""}</span>
          {t('importAccountingModal.stepResult')}
        </div>
      </div>

      <div className="mb-4">
        {step === "select" && (
          <>
            <label className="block font-semibold mb-2">{t('importAccountingModal.selectExcelFile')}</label>
            <input
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              disabled={step !== "select"}
            />
            {file && (
              <div className="mt-2 text-sm text-gray-700">
                {t('importAccountingModal.selectedFile')} <span className="font-medium">{file.name}</span>
              </div>
            )}
            {error && (
              <div className="mt-2 flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded px-2 py-1">
                <FaExclamationCircle className="text-red-500" />
                {error}
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-gray-100 text-xs text-gray-500">
              <p className="mb-2 font-medium text-gray-700">{t('importAccountingModal.noFileQuestion')}</p>
              <p className="mb-3">{t('importAccountingModal.downloadTemplateHint')}</p>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="text-[--color-primary] font-semibold hover:underline flex items-center gap-1.5 p-1 bg-gray-50 border border-gray-200 rounded cursor-pointer transition-colors hover:bg-gray-100"
              >
                {t('importAccountingModal.downloadImportTemplate')}
              </button>
            </div>
          </>
        )}

        {/* Étape mapping */}
        {step === "mapping" && columns.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 font-semibold text-blue-700">{t('importAccountingModal.mapFieldsHint')}</div>
            <table className="min-w-full text-xs mb-4">
              <thead>
                <tr>
                  <th className="px-2 py-1 border-b font-semibold text-left">{t('importAccountingModal.formField')}</th>
                  <th className="px-2 py-1 border-b font-semibold text-left">{t('importAccountingModal.fileColumn')}</th>
                </tr>
              </thead>
              <tbody>
                {FORM_FIELDS.map(field => (
                  <tr key={field.key}>
                    <td className="px-2 py-1 border-b">{field.label}</td>
                    <td className="px-2 py-1 border-b">
                      <select
                        className="border rounded px-1 py-0.5"
                        value={mapping[field.key] || ""}
                        onChange={e => handleMappingChange(field.key, e.target.value)}
                      >
                        <option value="">{t('importAccountingModal.ignoreOption')}</option>
                        {columns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-2 mt-2">
              <button
                className="px-4 py-1 rounded bg-gray-200 text-gray-700 text-sm"
                onClick={() => {
                  setStep("select");
                  setPreview(null);
                }}
                type="button"
              >
                {t('importAccountingModal.back')}
              </button>
              <button
                className="px-4 py-1 rounded bg-[--color-primary] text-white text-sm disabled:opacity-50"
                onClick={handlePreview}
                disabled={Object.values(mapping).filter(Boolean).length === 0}
              >
                {t('importAccountingModal.previewImport')}
              </button>
            </div>
          </div>
        )}

        {/* Étape prévisualisation */}
        {step === "preview" && preview && (
          <div className="mt-4">
            <div className="mb-2 font-semibold text-green-700">{t('importAccountingModal.previewLabel')}</div>
            <div className="max-h-64 overflow-auto border rounded bg-gray-50 p-2">
              <table className="min-w-full text-xs">
                <thead>
                  <tr>
                    {FORM_FIELDS.map(field => (
                      <th key={field.key} className="px-2 py-1 border-b font-semibold">{field.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, idx) => {
                    let isDefault = false;
                    Object.values(row).forEach(val => {
                      const valStr = String(val || "").toLowerCase();
                      if (valStr.startsWith("default_") || valStr.includes("code par défaut") || valStr.includes("code par defaut")) {
                        isDefault = true;
                      }
                    });
                    
                    return (
                      <tr key={idx} className={isDefault ? "bg-gray-100 font-semibold italic text-gray-700" : ""}>
                        {FORM_FIELDS.map(field => (
                          <td key={field.key} className="px-2 py-1 border-b">
                            {mapping[field.key] ? row[mapping[field.key]] : ""}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                className="px-4 py-1 rounded bg-gray-200 text-gray-700 text-sm"
                onClick={() => setStep("mapping")}
                type="button"
              >
                {t('importAccountingModal.back')}
              </button>
              <div className="mt-3 text-xs text-gray-500 flex-1">
                {t('importAccountingModal.clickToImportHint')}
              </div>
            </div>
          </div>
        )}

        {/* Étape compte rendu : ce que l'import fera, ligne par ligne, avant toute écriture */}
        {step === "report" && report && (
          <div className="mt-4" data-cy="import-report">
            <div className="mb-2 font-semibold text-gray-800">{t('importAccountingModal.reportTitle')}</div>
            <p className="text-xs text-gray-600 mb-3">{t('importAccountingModal.reportHint')}</p>
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              <button
                type="button"
                onClick={() => setReportFilter("all")}
                className={`px-2.5 py-1 rounded-full text-xs border ${reportFilter === "all" ? "bg-[--color-primary] text-white border-[--color-primary]" : "bg-white text-gray-700 border-gray-300"}`}
              >
                {t('importAccountingModal.reportAll', { count: reportLines.length })}
              </button>
              {reportStatuses.map((status) => (
                <button
                  key={status.id}
                  type="button"
                  onClick={() => setReportFilter(status.id)}
                  className={`px-2.5 py-1 rounded-full text-xs border ${reportFilter === status.id ? "ring-2 ring-[--color-primary]/40" : ""} ${status.tone}`}
                  data-cy={`import-report-filter-${status.id}`}
                >
                  {t(`importAccountingModal.status_${status.id}`)} ({report.counts?.[status.id] || 0})
                </button>
              ))}
              <div className="relative flex-1 min-w-[160px]">
                <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={11} />
                <input
                  type="search"
                  value={reportSearch}
                  onChange={(e) => setReportSearch(e.target.value)}
                  placeholder={t('importAccountingModal.reportSearch')}
                  className="w-full pl-7 pr-2 py-1 border rounded text-xs"
                  data-cy="import-report-search"
                />
              </div>
            </div>
            <div className="max-h-72 overflow-auto border rounded">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold w-14">{t('importAccountingModal.reportRow')}</th>
                    <th className="px-2 py-1.5 text-left font-semibold">{t('importAccountingModal.colName')}</th>
                    <th className="px-2 py-1.5 text-left font-semibold">{t('importAccountingModal.reportStatus')}</th>
                    <th className="px-2 py-1.5 text-left font-semibold">{t('importAccountingModal.reportDetail')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {visibleReportLines.length === 0 ? (
                    <tr><td colSpan={4} className="px-2 py-6 text-center text-gray-500">{t('importAccountingModal.reportEmpty')}</td></tr>
                  ) : visibleReportLines.map((line, idx) => (
                    <tr key={idx} data-cy="import-report-line">
                      <td className="px-2 py-1.5 text-gray-500">{line.row_number || ""}</td>
                      <td className="px-2 py-1.5">
                        {line.name || "—"}
                        {line.code && <span className="text-gray-500"> ({line.code})</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`px-2 py-0.5 rounded-full border whitespace-nowrap ${statusTone[line.status] || ""}`}>
                          {t(`importAccountingModal.status_${line.status}`)}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        {line.changes.map((change, cIdx) => (
                          <div key={cIdx}>
                            {t(change.side === "debit" ? 'importAccountingModal.reportDebit' : 'importAccountingModal.reportCredit')}
                            {' : '}
                            <span className="font-mono">{change.from || t('importAccountingModal.reportNone')}</span>
                            {' → '}
                            <span className="font-mono font-semibold">{change.to || t('importAccountingModal.reportInherited')}</span>
                          </div>
                        ))}
                        {line.unknown_codes.length > 0 && (
                          <div className="text-red-700">
                            {t('importAccountingModal.reportUnknownCodes', { codes: line.unknown_codes.join(', ') })}
                          </div>
                        )}
                        {line.status === "not_found" && (
                          <div className="text-orange-700">{t('importAccountingModal.reportNotFound')}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {error && (
              <div className="mt-2 flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded px-2 py-1">
                <FaExclamationCircle className="text-red-500" />
                {error}
              </div>
            )}
            <div className="flex gap-2 mt-3 items-center">
              <button
                className="px-4 py-1 rounded bg-gray-200 text-gray-700 text-sm"
                onClick={() => setStep("preview")}
                type="button"
              >
                {t('importAccountingModal.back')}
              </button>
              <span className="text-xs text-gray-500">
                {reportChangeCount === 0 ? t('importAccountingModal.reportNothingToImport') : t('importAccountingModal.reportNothingWrittenYet')}
              </span>
            </div>
          </div>
        )}
        {step === "result" && (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-yellow-600 font-semibold mb-3">
              <FaExclamationCircle className="text-yellow-500" size={18} />
              <span>{t('importAccountingModal.importWithWarnings')}</span>
            </div>

            <p className="text-sm text-gray-700 mb-4">{successMsg}</p>

            <div className="bg-yellow-50 border border-yellow-200 rounded p-4 text-xs text-yellow-800 mb-4 max-h-48 overflow-auto">
              <p className="font-semibold mb-2">{t('importAccountingModal.accountsNotAssociatedHint')}</p>
              <ul className="list-disc pl-4 space-y-1 font-mono">
                {warnings.map((acc, idx) => (
                  <li key={idx}>{acc}</li>
                ))}
              </ul>
              <p className="mt-3 text-[11px] text-yellow-700 italic">{t('importAccountingModal.addManuallyHint')}</p>
            </div>
            
            <div className="flex justify-end mt-4 pt-3 border-t border-gray-100">
              <button
                className="px-6 py-2 rounded bg-[--color-primary] text-white text-sm font-semibold transition-colors hover:opacity-90"
                onClick={() => {
                  setFile(null);
                  setColumns([]);
                  setMapping({});
                  setPreview(null);
                  setReport(null);
                  setStep("select");
                  setError("");
                  setWarnings([]);
                  setSuccessMsg("");
                  onClose();
                }}
                type="button"
              >
                {t('importAccountingModal.closeAndRefresh')}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ImportAccountingModal;